import type Hls from 'hls.js'

/** Backend's own NETRADYNE_LIVE_SITES (serving/app.py) -- kept in sync by hand, same as
 *  cc/NetradyneCameraButton.tsx's copy of this type (that one for React card popups,
 *  this one for maplibre's raw-HTML map popups -- see this module's own docstring). */
export type NetradyneSite = 'ultratech' | 'mbmt' | 'star_cement' | 'gti' | 'jm_baxi'

const BTN_CLASS = 'netradyne-cam-btn'
const PANEL_CLASS = 'netradyne-cam-panel'
const LIVE_BTN_CLASS = 'netradyne-cam-live-btn'
const CLIP_BTN_CLASS = 'netradyne-cam-clip-btn'
const CAMERA_ICON = (
  `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25">`
  + `<path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`
)

let keyframesInjected = false
/** @keyframes aren't expressible as an inline style attribute -- this is the one-time,
 *  idempotent style-tag injection every renderPanel() loading/live state below depends
 *  on for its spinner and the LIVE badge's pulse. Matches cc/NetradyneCameraButton.tsx's
 *  animate-spin/animate-pulse, just hand-written since there's no Tailwind here. */
function ensureKeyframes(): void {
  if (keyframesInjected || document.getElementById('netradyne-cam-keyframes')) return
  keyframesInjected = true
  const style = document.createElement('style')
  style.id = 'netradyne-cam-keyframes'
  style.textContent = (
    '@keyframes netradyne-spin { to { transform: rotate(360deg); } }'
    + '@keyframes netradyne-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }'
  )
  document.head.appendChild(style)
}

/** HTML to splice into any maplibre popup string (see vehiclePopup.ts, MbmtLiveMap.tsx,
 *  EimPortsMap.tsx) -- every map popup in this codebase is a raw HTML string handed to
 *  maplibre's Popup, not a React tree, so this toggles via plain DOM listeners attached
 *  after setHTML/addTo rather than React state. See attachNetradyneCameraHandlers. */
export function netradyneCameraButtonHtml(): string {
  ensureKeyframes()
  return (
    `<div style="position:relative;display:inline-block;margin-top:8px">`
    + `<button type="button" class="${BTN_CLASS}" `
    + `style="width:100%;display:flex;align-items:center;justify-content:center;gap:6px;`
    + `padding:6px 10px;border-radius:8px;border:1px solid rgba(15,23,42,0.12);`
    + `background:#0F172A;color:#fff;font:600 11px Inter,sans-serif;cursor:pointer">`
    + CAMERA_ICON
    + `Camera</button>`
    // Same "there's a live feed behind this" recording-dot language as the React
    // version's trigger button -- a plain camera glyph with no cue reads as just another
    // row action, not specifically a live-video entry point.
    + `<span style="position:absolute;top:-3px;right:-3px;width:9px;height:9px;`
    + `border-radius:999px;background:#DC2626;border:2px solid #fff"></span>`
    + `</div>`
    + `<div class="${PANEL_CLASS}" style="display:none;margin-top:6px"></div>`
  )
}

interface FetchState {
  loading: boolean
  error: string | null
  hlsUrl: string | null
}

/** Confirmed live against the real Netradyne response (both Ultratech and MBMT):
 *  {"data": {"liveStreamingHlsUrls": [{"url": "...", "position": 0}]}}. Clip's shape is
 *  still unconfirmed (501 placeholder) -- the hlsUrl/url/streamUrl guesses stay as a
 *  fallback for whenever that one goes live and turns out to use a different field. */
function extractHlsUrl(json: unknown): string | null {
  if (!json || typeof json !== 'object') return null
  const j = json as Record<string, unknown>
  const data = j.data && typeof j.data === 'object' ? (j.data as Record<string, unknown>) : undefined
  const liveUrls = data?.liveStreamingHlsUrls
  if (Array.isArray(liveUrls)) {
    const first = liveUrls.find((u): u is { url: string } => !!u && typeof u === 'object' && typeof (u as { url?: unknown }).url === 'string')
    if (first) return first.url
  }
  for (const c of [j.hlsUrl, j.url, j.streamUrl, data?.hlsUrl, data?.url]) {
    if (typeof c === 'string' && c) return c
  }
  return null
}

/** Same caveat as cc/NetradyneCameraButton.tsx's copy: Netradyne's own error body is
 *  {"error": {"message": "...", ...}} -- an OBJECT, not a string (confirmed live, status
 *  400 "Live stream URL is currently in use"). String(j.error) on that renders literally
 *  as "[object Object]"; pull .message out of it instead. */
function extractErrorMessage(json: unknown, fallback: string): string {
  if (json && typeof json === 'object') {
    const err = (json as Record<string, unknown>).error
    if (typeof err === 'string' && err) return err
    if (err && typeof err === 'object') {
      const msg = (err as Record<string, unknown>).message
      if (typeof msg === 'string' && msg) return msg
    }
  }
  return fallback
}

async function fetchStream(path: string, body: Record<string, unknown>): Promise<FetchState> {
  try {
    const r = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const j = await r.json().catch(() => ({}))
    const url = r.ok ? extractHlsUrl(j) : null
    return {
      loading: false,
      error: r.ok ? (url ? null : 'No playable URL in the response') : extractErrorMessage(j, String(r.status)),
      hlsUrl: url,
    }
  } catch (e) {
    return { loading: false, error: e instanceof Error ? e.message : 'request failed', hlsUrl: null }
  }
}

/** Wires the button rendered by netradyneCameraButtonHtml() inside one popup's live DOM
 *  node. Must be called again every time the popup's HTML is replaced (setHTML tears
 *  down the old DOM and its listeners) -- same requirement EIM's own
 *  MARK_BREAKDOWN_BTN_CLASS wiring already documents at its call site. */
export function attachNetradyneCameraHandlers(
  popupEl: HTMLElement,
  site: NetradyneSite,
  vehicleNumber: string,
  alertId?: number | null,
): void {
  const btn = popupEl.querySelector<HTMLButtonElement>(`.${BTN_CLASS}`)
  const panelEl = popupEl.querySelector<HTMLDivElement>(`.${PANEL_CLASS}`)
  if (!btn || !panelEl) return
  const panel = panelEl

  let hls: Hls | null = null
  let shown = false
  let tab: 'live' | 'clip' = 'live'

  function renderPanel(state: FetchState) {
    hls?.destroy()
    hls = null
    panel.innerHTML = ''
    panel.style.cssText = (
      'margin-top:6px;border-radius:10px;border:1px solid rgba(15,23,42,0.08);background:#fff;'
      + 'box-shadow:0 8px 20px rgba(15,23,42,0.16);overflow:hidden'
    )

    const tabs = document.createElement('div')
    tabs.style.cssText = 'display:flex;gap:4px;padding:6px'
    tabs.innerHTML = (
      `<button type="button" class="${LIVE_BTN_CLASS}" style="flex:1;padding:6px;border:none;border-radius:6px;`
      + `font:600 10px Inter,sans-serif;cursor:pointer;transition:background 0.15s;`
      + `background:${tab === 'live' ? '#0F172A' : '#F1F5F9'};`
      + `color:${tab === 'live' ? '#fff' : '#64748B'}">Live</button>`
      + `<button type="button" class="${CLIP_BTN_CLASS}" style="flex:1;padding:6px;border:none;border-radius:6px;`
      + `font:600 10px Inter,sans-serif;cursor:${alertId == null ? 'not-allowed' : 'pointer'};transition:background 0.15s;`
      + `opacity:${alertId == null ? 0.4 : 1};background:${tab === 'clip' ? '#0F172A' : '#F1F5F9'};`
      + `color:${tab === 'clip' ? '#fff' : '#64748B'}" ${alertId == null ? 'disabled' : ''}>Clip</button>`
    )
    panel.appendChild(tabs)
    tabs.querySelector(`.${LIVE_BTN_CLASS}`)?.addEventListener('click', () => { tab = 'live'; void run() })
    tabs.querySelector(`.${CLIP_BTN_CLASS}`)?.addEventListener('click', () => {
      if (alertId == null) return
      tab = 'clip'; void run()
    })

    const body = document.createElement('div')
    body.style.cssText = 'padding:0 6px 6px'
    panel.appendChild(body)

    if (state.loading) {
      body.innerHTML = (
        `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;`
        + `height:120px;font:11px Inter,sans-serif;color:#94A3B8">`
        + `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"`
        + ` stroke-linecap="round" style="animation:netradyne-spin 0.8s linear infinite">`
        + `<path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`
        + `Connecting…</div>`
      )
      return
    }
    if (state.hlsUrl) {
      const frame = document.createElement('div')
      frame.style.cssText = 'position:relative;border-radius:8px;overflow:hidden;background:#000'
      if (tab === 'live') {
        const badge = document.createElement('span')
        badge.style.cssText = (
          'position:absolute;left:6px;top:6px;z-index:1;display:flex;align-items:center;gap:4px;'
          + 'padding:2px 6px;border-radius:999px;background:rgba(0,0,0,0.6);backdrop-filter:blur(2px);'
          + "font:700 9px Inter,sans-serif;letter-spacing:0.04em;text-transform:uppercase;color:#fff"
        )
        badge.innerHTML = (
          '<span style="width:6px;height:6px;border-radius:999px;background:#DC2626;'
          + 'animation:netradyne-pulse 1.4s ease-in-out infinite"></span>Live'
        )
        frame.appendChild(badge)
      }
      const video = document.createElement('video')
      video.controls = true
      video.autoplay = true
      video.muted = true
      video.style.cssText = 'display:block;width:100%;aspect-ratio:16/9'
      frame.appendChild(video)
      body.appendChild(frame)
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = state.hlsUrl
      } else {
        const url = state.hlsUrl
        void import('hls.js').then(({ default: HlsCtor }) => {
          if (!HlsCtor.isSupported()) return
          hls?.destroy()
          hls = new HlsCtor()
          hls.loadSource(url)
          hls.attachMedia(video)
        })
      }
      return
    }
    body.innerHTML = (
      `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;`
      + `height:120px;padding:0 8px;text-align:center">`
      + `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" stroke-width="2">`
      + `<path d="m2 2 20 20M9.5 6H17a2 2 0 0 1 2 2v3.5m-.85 4.85A2 2 0 0 1 16 17H5a2 2 0 0 1-2-2V8a2 2 0 0 1 .59-1.41"/>`
      + `<path d="m22 8-6 4 6 4V8z"/></svg>`
      + `<span style="font:11px Inter,sans-serif;color:#64748B">`
      + `${state.error ?? (tab === 'clip' ? 'Coming soon' : 'No feed yet')}</span></div>`
    )
  }

  async function run() {
    renderPanel({ loading: true, error: null, hlsUrl: null })
    const state = tab === 'live'
      ? await fetchStream('/api/netradyne/live-stream', { site, vehicle_number: vehicleNumber })
      : await fetchStream('/api/netradyne/alert-clip', { site, vehicle_number: vehicleNumber, alert_id: alertId })
    renderPanel(state)
  }

  btn.addEventListener('click', () => {
    shown = !shown
    panel.style.display = shown ? 'block' : 'none'
    if (!shown) {
      hls?.destroy(); hls = null
      panel.innerHTML = ''
      return
    }
    void run()
  })
}
